import type { ReactNode } from 'react';
import type { CharacterId } from '../../theater/catalog';

/** Rear-view black silhouette busts with distinct shapes per character_id. */
export default function CharacterSilhouette({
  character,
  className,
}: {
  character: CharacterId;
  className?: string;
}) {
  const id = (character in SILHOUETTES ? character : 'normal') as CharacterId;
  return (
    <svg className={className} viewBox="0 0 64 80" role="img" aria-hidden>
      {SILHOUETTES[id]}
    </svg>
  );
}

const fill = '#0a0a0a';

const shoulders = (
  <path d="M8 78 C10 52, 18 46, 32 46 C46 46, 54 52, 56 78 Z" fill={fill} />
);

const SILHOUETTES: Record<CharacterId, ReactNode> = {
  normal: (
    <>
      <ellipse cx="32" cy="28" rx="16" ry="18" fill={fill} />
      {shoulders}
    </>
  ),
  gamer: (
    <>
      <path
        d="M14 30 C14 14, 50 14, 50 30"
        fill="none"
        stroke={fill}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <rect x="8" y="24" width="10" height="16" rx="4" fill={fill} />
      <rect x="46" y="24" width="10" height="16" rx="4" fill={fill} />
      <ellipse cx="32" cy="30" rx="15" ry="16" fill={fill} />
      {shoulders}
    </>
  ),
  nerd: (
    <>
      <ellipse cx="32" cy="28" rx="15" ry="17" fill={fill} />
      <path d="M18 18 C22 8, 42 8, 46 18 L44 22 C40 14, 24 14, 20 22 Z" fill={fill} />
      {shoulders}
    </>
  ),
  goth: (
    <>
      <path
        d="M16 34 L18 12 L24 22 L28 8 L32 20 L36 6 L40 22 L46 10 L48 34 Z"
        fill={fill}
      />
      <ellipse cx="32" cy="32" rx="14" ry="15" fill={fill} />
      {shoulders}
    </>
  ),
  punk: (
    <>
      <path d="M28 40 L30 4 L34 4 L36 40 Z" fill={fill} />
      <ellipse cx="32" cy="32" rx="14" ry="15" fill={fill} />
      {shoulders}
    </>
  ),
  'old-guy': (
    <>
      <ellipse cx="32" cy="30" rx="16" ry="17" fill={fill} />
      <path d="M14 28 C12 40, 18 44, 22 40 Z" fill={fill} />
      <path d="M50 28 C52 40, 46 44, 42 40 Z" fill={fill} />
      {shoulders}
    </>
  ),
  alien: (
    <>
      <ellipse cx="32" cy="26" rx="20" ry="24" fill={fill} />
      <line x1="32" y1="4" x2="32" y2="14" stroke={fill} strokeWidth="3" />
      <circle cx="32" cy="3" r="3" fill={fill} />
      <path d="M14 78 C16 54, 20 48, 32 48 C44 48, 48 54, 50 78 Z" fill={fill} />
    </>
  ),
  robot: (
    <>
      <rect x="18" y="12" width="28" height="28" rx="4" fill={fill} />
      <line x1="32" y1="4" x2="32" y2="12" stroke={fill} strokeWidth="3" />
      <circle cx="32" cy="3" r="3" fill={fill} />
      <rect x="10" y="48" width="44" height="30" rx="4" fill={fill} />
      <rect x="26" y="40" width="12" height="10" fill={fill} />
    </>
  ),
  skeleton: (
    <>
      <ellipse cx="32" cy="26" rx="15" ry="17" fill={fill} />
      <path d="M12 78 C14 52, 20 50, 32 50 C44 50, 50 52, 52 78 Z" fill={fill} />
    </>
  ),
  gopher: (
    <>
      <ellipse cx="22" cy="20" rx="5" ry="6" fill={fill} />
      <ellipse cx="42" cy="20" rx="5" ry="6" fill={fill} />
      <ellipse cx="32" cy="32" rx="18" ry="20" fill={fill} />
      <path d="M10 78 C12 56, 18 50, 32 50 C46 50, 52 56, 54 78 Z" fill={fill} />
    </>
  ),
  cat: (
    <>
      <path d="M16 28 L20 8 L28 22 Z" fill={fill} />
      <path d="M48 28 L44 8 L36 22 Z" fill={fill} />
      <ellipse cx="32" cy="30" rx="16" ry="16" fill={fill} />
      {shoulders}
    </>
  ),
  'hot-dog': (
    <>
      <ellipse cx="32" cy="36" rx="14" ry="28" fill={fill} />
      <ellipse cx="32" cy="20" rx="10" ry="8" fill={fill} />
      <path d="M16 78 C18 58, 22 54, 32 54 C42 54, 46 58, 48 78 Z" fill={fill} />
    </>
  ),
};
